import { useMemo } from 'react';
import { BusinessUnit } from 'data/BusinessUnit';
import User, { SHRole } from 'data/User';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';
import { convertCamelCaseToSpaced } from 'utils/string';
import Vendor from 'data/Vendor';

interface UseUserRoleProps {
  user: User | null;
  selectedVendor: Vendor | null | undefined;
  selectedBusinessUnit: BusinessUnit | null | undefined;
}

interface UseUserRoleReturn {
  userRole: string;
  displayUserRole: string;
  roles: SHRole[];
}

/**
 * Custom hook for managing user role display and business unit role resolution
 * 
 * Handles:
 * - Role resolution from business unit or user
 * - Display formatting for user role
 * - Session storage role retrieval
 * - Memoized role calculations
 * 
 * @param props - User and business unit data
 * @returns User role information and display values
 */
export const useUserRole = ({
  user,
  selectedVendor,
  selectedBusinessUnit
}: UseUserRoleProps): UseUserRoleReturn => {

  // Get roles from session storage
  const roles: SHRole[] = useMemo(() => {
    return getItems(STORAGE_KEYS.ROLES) || [];
  }, []);

  // Determine user role from business unit or fallback to user role
  const userRole = useMemo(() => {
    const userProfiles = user?.userProfiles || [];
    return userProfiles.length > 0 ? userProfiles.find(profile => profile.vendorId == null && profile.businessUnitId == null)?.role?.name ?? //admin
      userProfiles.find(profile => profile.vendorId == selectedVendor?.id && profile.businessUnitId == null)?.role?.name ?? // vendor admin
      userProfiles.find(profile => profile.vendorId == selectedVendor?.id && profile.businessUnitId == selectedBusinessUnit?.id)?.role?.name ?? // functional role
      '' : user?.role ?? ''; // or legacy check
  }, [user, selectedBusinessUnit, selectedVendor]);

  // Create display-friendly version of user role
  const displayUserRole = useMemo(() => {
    return convertCamelCaseToSpaced(userRole);
  }, [userRole]);

  return {
    userRole,
    displayUserRole,
    roles
  };
};